import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemSecondaryAction,
  ListItemText,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import VisibilityIcon from "@mui/icons-material/Visibility";
import LibraryBooksIcon from "@mui/icons-material/LibraryBooks";
import DeleteIcon from "@mui/icons-material/Delete";
import { useAssignmentAuthoring } from "./AssignmentAuthoringProvider";
import DropdownLibraryDialog from "./DropdownLibraryDialog";

const formatSectionType = (section) => {
  if (!section) {
    return "";
  }

  const template = section.template || section.type;
  if (!template) {
    return "Custom";
  }

  return template
    .split("-")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

const getSecondaryContent = (section) => {
  const typeLabel = formatSectionType(section);

  if (!typeLabel) {
    return null;
  }

  return (
    <Typography
      variant="caption"
      color="text.secondary"
      component="span"
      display="block"
    >
      {typeLabel}
    </Typography>
  );
};

const AssignmentSidebar = ({
  sections,
  collapsedSections,
  onToggleSection,
  onPreviewSection,
  onPreviewAssignment,
  onAddSection,
  onScrollToSection,
  onSelectSection,
  activeSectionId,
}) => {
  const { updateSection } = useAssignmentAuthoring();
  const [dropdownManagerSectionId, setDropdownManagerSectionId] = useState(null);
  const dropdownManagerOpen = Boolean(dropdownManagerSectionId);

  const activeDropdownSection = useMemo(() => {
    if (!dropdownManagerSectionId || !Array.isArray(sections)) {
      return null;
    }

    return (
      sections.find((section) => section.id === dropdownManagerSectionId) || null
    );
  }, [dropdownManagerSectionId, sections]);

  useEffect(() => {
    if (!dropdownManagerSectionId) {
      return;
    }

    if (!activeDropdownSection) {
      setDropdownManagerSectionId(null);
    }
  }, [activeDropdownSection, dropdownManagerSectionId]);

  const handleOpenDropdownManager = useCallback((sectionId) => {
    setDropdownManagerSectionId(sectionId);
  }, []);

  const handleCloseDropdownManager = useCallback(() => {
    setDropdownManagerSectionId(null);
  }, []);

  const handleDropdownLibraryChange = useCallback(
    (sectionId, dropdowns) => {
      if (!updateSection || !sectionId) {
        return;
      }

      const nextDropdowns = Array.isArray(dropdowns) ? dropdowns : [];

      updateSection(sectionId, (prev) => {
        const previousDropdowns = Array.isArray(prev.metadata?.dropdowns)
          ? prev.metadata.dropdowns
          : [];
        const serializedPrev = JSON.stringify(previousDropdowns);
        const serializedNext = JSON.stringify(nextDropdowns);

        if (serializedPrev === serializedNext) {
          return prev;
        }

        const nextMetadata = { ...(prev.metadata || {}) };
        nextMetadata.dropdowns = nextDropdowns;

        return {
          ...prev,
          metadata: nextMetadata,
        };
      });
    },
    [updateSection]
  );

  const hasSections = Array.isArray(sections) && sections.length > 0;

  const tableSections = useMemo(
    () =>
      Array.isArray(sections)
        ? sections.filter((section) => (section?.template || section?.type) === "table")
        : [],
    [sections]
  );

  const dropdownSummaries = useMemo(() => {
    return tableSections.map((section) => {
      const dropdowns = Array.isArray(section?.metadata?.dropdowns)
        ? section.metadata.dropdowns
        : [];

      const entries = dropdowns
        .map((dropdown, index) =>
          dropdown
            ? {
                dropdown,
                dropdownIndex: index,
                sectionId: section.id,
                sectionTitle: section.title || "Untitled section",
              }
            : null
        )
        .filter(Boolean);

      return {
        section,
        entries,
      };
    });
  }, [tableSections]);

  const handleRemoveDropdown = useCallback(
    (sectionId, dropdownIndex) => {
      if (!updateSection || !sectionId || dropdownIndex == null) {
        return;
      }

      updateSection(sectionId, (prev) => {
        const previousDropdowns = Array.isArray(prev.metadata?.dropdowns)
          ? prev.metadata.dropdowns
          : [];

        if (dropdownIndex < 0 || dropdownIndex >= previousDropdowns.length) {
          return prev;
        }

        const nextDropdowns = previousDropdowns.filter((_, index) => index !== dropdownIndex);

        const serializedPrev = JSON.stringify(previousDropdowns);
        const serializedNext = JSON.stringify(nextDropdowns);

        if (serializedPrev === serializedNext) {
          return prev;
        }

        const nextMetadata = { ...(prev.metadata || {}) };
        nextMetadata.dropdowns = nextDropdowns;

        return {
          ...prev,
          metadata: nextMetadata,
        };
      });
    },
    [updateSection]
  );

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        width: { xs: "100%", lg: 320 },
        position: { lg: "sticky" },
        top: { lg: 24 },
        maxHeight: { lg: "80vh" },
        overflowY: "auto",
      }}
    >
      <Stack spacing={2}>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }} gutterBottom>
            Assignment outline
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Select a section to load it into the workspace, collapse editors, or
            preview your assignment.
          </Typography>
        </Box>
        <Stack spacing={1.5}>
          <Button
            variant="contained"
            color="primary"
            onClick={onPreviewAssignment}
            fullWidth
          >
            Preview assignment
          </Button>
          <Paper variant="outlined" sx={{ p: 1.5 }}>
            <Stack spacing={1}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Dropdown library
              </Typography>
              {dropdownSummaries.length ? (
                <Stack spacing={1.5}>
                  {dropdownSummaries.map(({ section, entries }) => (
                    <Box key={section.id}>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        spacing={1}
                        sx={{ mb: 0.5 }}
                      >
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontWeight: 600 }}
                        >
                          {section.title || "Untitled section"}
                        </Typography>
                        <Button
                          size="small"
                          startIcon={<LibraryBooksIcon fontSize="small" />}
                          onClick={() => handleOpenDropdownManager(section.id)}
                        >
                          Add dropdown
                        </Button>
                      </Stack>
                      {entries.length ? (
                        <List dense disablePadding>
                          {entries.map(({ dropdown, dropdownIndex }) => {
                            const optionCount = Array.isArray(dropdown.options)
                              ? dropdown.options.length
                              : 0;
                            const optionLabel = `${optionCount} option${optionCount === 1 ? "" : "s"}`;
                            const dropdownName = dropdown.name || `Dropdown ${dropdownIndex + 1}`;

                            return (
                              <ListItem
                                key={`${section.id}-dropdown-${dropdown.id || dropdownIndex}`}
                                disableGutters
                                secondaryAction={
                                  <Tooltip title="Delete dropdown">
                                    <IconButton
                                      edge="end"
                                      size="small"
                                      onClick={() => handleRemoveDropdown(section.id, dropdownIndex)}
                                      aria-label={`Delete ${dropdownName}`}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                }
                              >
                                <ListItemText
                                  primary={dropdownName}
                                  secondary={optionLabel}
                                  primaryTypographyProps={{ variant: "body2" }}
                                  secondaryTypographyProps={{ variant: "caption" }}
                                />
                              </ListItem>
                            );
                          })}
                        </List>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No dropdowns yet. Use the button above to add dropdowns for this table.
                          </Typography>
                        )}
                    </Box>
                  ))}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Add a table section to create dropdowns that can be reused across your
                  assignment.
                </Typography>
              )}
            </Stack>
          </Paper>
          <Button variant="outlined" onClick={onAddSection} fullWidth>
            Add section
          </Button>
        </Stack>
        <Divider />
        {hasSections ? (
          <List dense>
            {sections.map((section, index) => {
              const isCollapsed = !!collapsedSections?.[section.id];
              return (
                <ListItem
                  key={section.id}
                  disablePadding
                  sx={{ alignItems: "stretch" }}
                >
                  <ListItemButton
                    onClick={() => {
                      onSelectSection?.(section.id);
                      onScrollToSection?.(section.id);
                    }}
                    sx={{ pr: 8 }}
                    selected={section.id === activeSectionId}
                  >
                    <ListItemText
                      primary={`Section ${index + 1}: ${
                        section.title || "Untitled section"
                      }`}
                      secondary={getSecondaryContent(section)}
                      secondaryTypographyProps={{
                        component: "span",
                      }}
                    />
                  </ListItemButton>
                  <ListItemSecondaryAction>
                    <Tooltip
                      title={isCollapsed ? "Expand section" : "Collapse section"}
                    >
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={() => onToggleSection?.(section.id)}
                        sx={{ mr: 0.5 }}
                        aria-label={`${isCollapsed ? "Expand" : "Collapse"} ${
                          section.title || "section"
                        }`}
                      >
                        {isCollapsed ? (
                          <ExpandMoreIcon fontSize="small" />
                        ) : (
                          <ExpandLessIcon fontSize="small" />
                        )}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Preview section">
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={() => onPreviewSection?.(section.id)}
                        aria-label={`Preview ${section.title || "section"}`}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </ListItemSecondaryAction>
                </ListItem>
              );
            })}
          </List>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Add a section to get started. Your outline will appear here for easy
            navigation.
          </Typography>
        )}
      </Stack>
      <DropdownLibraryDialog
        open={dropdownManagerOpen && Boolean(activeDropdownSection)}
        section={activeDropdownSection}
        onClose={handleCloseDropdownManager}
        onChange={handleDropdownLibraryChange}
      />
    </Paper>
  );
};

export default AssignmentSidebar;
